import {
  useCallback,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { InputSpec } from '@guess/engine';
import { hslToHex } from '../ui/color';
import type { AnswerProps } from './AnswerRenderer';
import s from './input.module.scss';

type ColorSpec = Extract<InputSpec, { kind: 'color' }>;

/**
 * La roue de couleurs — en realite une bande de teintes et un carre
 * saturation / luminosite.
 *
 * Le disque serait plus joli et nettement moins jouable : sur un disque, un
 * geste maladroit change la teinte ET la saturation d'un coup, alors que
 * corriger « un peu plus sombre » est exactement ce qu'on veut pouvoir faire
 * de memoire. Deux commandes separees permettent d'ajuster un axe sans perdre
 * l'autre — et ca compte, parce que ce mini-jeu accorde du credit partiel :
 * s'approcher rapporte deja.
 *
 * Le rendu vient de degrades CSS, pas d'un canvas : rien a redessiner, et les
 * deux commandes repondent aux fleches du clavier comme de vrais curseurs.
 */
export function ColorAnswer({ disabled, onSubmit }: AnswerProps<ColorSpec>) {
  const [hue, setHue] = useState(200);
  const [sat, setSat] = useState(0.7);
  const [light, setLight] = useState(0.5);

  const hex = hslToHex(hue, sat, light);

  const hueRef = useRef<HTMLDivElement | null>(null);
  const padRef = useRef<HTMLDivElement | null>(null);

  /** Position du pointeur dans un element, ramenee a 0..1 sur les deux axes. */
  const track = useCallback((element: HTMLElement, clientX: number, clientY: number) => {
    const rect = element.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
    };
  }, []);

  const onHuePointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    // Suivre le geste meme s'il sort de la bande : sans capture, la teinte se
    // fige des que le doigt deborde, ce qui donne une sensation de commande cassee.
    if (event.buttons === 0 && event.type === 'pointermove') return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const element = hueRef.current;
    if (!element) return;
    setHue(track(element, event.clientX, event.clientY).x * 360);
  };

  const onPadPointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (event.buttons === 0 && event.type === 'pointermove') return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const element = padRef.current;
    if (!element) return;
    const { x, y } = track(element, event.clientX, event.clientY);
    setSat(x);
    setLight(1 - y); // le haut est clair, comme dans tous les selecteurs
  };

  const nudge = (event: ReactKeyboardEvent, axis: 'hue' | 'sat' | 'light') => {
    const step = event.shiftKey ? 10 : 1;
    const dir = event.key === 'ArrowRight' || event.key === 'ArrowUp' ? 1
      : event.key === 'ArrowLeft' || event.key === 'ArrowDown' ? -1
      : 0;
    if (dir === 0) return;
    event.preventDefault();
    if (axis === 'hue') setHue((h) => (h + dir * step * 3.6 + 360) % 360);
    if (axis === 'sat') setSat((v) => Math.min(1, Math.max(0, v + dir * step * 0.01)));
    if (axis === 'light') setLight((v) => Math.min(1, Math.max(0, v + dir * step * 0.01)));
  };

  return (
    <div className={s.colorStage} style={{ '--picked': hex } as CSSProperties}>
      <div className={s.colorPreview}>
        <div className={s.colorChip} role="img" aria-label={`Couleur choisie ${hex}`} />
        {/* La valeur hexadecimale n'est PAS affichee : elle transformerait un
            exercice de memoire en exercice de recopie entre amis. */}
        <p className={s.colorHint}>Ajuste jusqu'a retrouver la teinte.</p>
      </div>

      <div className={s.colorControls}>
        <div
          ref={padRef}
          className={s.colorPad}
          style={{ '--hue': hue } as CSSProperties}
          onPointerDown={onPadPointer}
          onPointerMove={onPadPointer}
          onKeyDown={(event) => nudge(event, event.shiftKey ? 'light' : 'sat')}
          tabIndex={disabled ? -1 : 0}
          role="application"
          aria-label="Saturation et luminosite. Fleches pour la saturation, Maj et fleches pour la luminosite."
        >
          <span
            className={s.colorCursor}
            style={{ left: `${sat * 100}%`, top: `${(1 - light) * 100}%` }}
          />
        </div>

        <div
          ref={hueRef}
          className={s.colorHue}
          onPointerDown={onHuePointer}
          onPointerMove={onHuePointer}
          onKeyDown={(event) => nudge(event, 'hue')}
          tabIndex={disabled ? -1 : 0}
          role="slider"
          aria-label="Teinte"
          aria-valuemin={0}
          aria-valuemax={360}
          aria-valuenow={Math.round(hue)}
        >
          <span className={s.colorCursor} style={{ left: `${(hue / 360) * 100}%`, top: '50%' }} />
        </div>
      </div>

      <button
        type="button"
        className={s.submit}
        disabled={disabled}
        onClick={() => onSubmit({ kind: 'color', hex })}
      >
        Valider cette couleur
      </button>
    </div>
  );
}
